'use client';
import React, { useState } from 'react';
import { Lock, PlayCircle, CheckCircle2, Clock, BookOpen, Bot, Tag } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { Course } from './CourseCatalogContent';

const wingVariantMap: Record<string, 'foundations' | 'intermediate' | 'advanced' | 'professional'> = {
  foundations: 'foundations',
  intermediate: 'intermediate',
  advanced: 'advanced',
  professional: 'professional',
};

const wingBgMap: Record<string, string> = {
  foundations: 'wing-foundations-bg',
  intermediate: 'wing-intermediate-bg',
  advanced: 'wing-advanced-bg',
  professional: 'wing-professional-bg',
};

const wingAccentMap: Record<string, string> = {
  foundations: 'bg-emerald-500',
  intermediate: 'bg-blue-500',
  advanced: 'bg-purple-500',
  professional: 'bg-amber-500',
};

export default function CourseCard({ course }: { course: Course }) {
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(course.enrolled);

  const handleEnroll = async () => {
    if (course.locked) { setPermissionModalOpen(true); return; }
    setEnrolling(true);
    // BACKEND INTEGRATION: POST /api/courses/enroll with courseId, studentId
    await new Promise((r) => setTimeout(r, 800));
    setEnrolling(false);
    setEnrolled(true);
  };

  return (
    <>
      <div className={`relative flex flex-col rounded-2xl border border-border overflow-hidden card-hover bg-card`}>
        {/* Wing color header */}
        <div className={`h-2 w-full ${wingAccentMap[course.wing]}`} />

        <div className="p-5 flex flex-col flex-1">
          {/* Badges row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Badge variant={wingVariantMap[course.wing]}>
              {course.wing.charAt(0).toUpperCase() + course.wing.slice(1)}
            </Badge>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Ages {course.ageRange}</span>
            {course.locked && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                <Lock size={10} /> Locked
              </span>
            )}
            {enrolled && !course.locked && (
              <span className="flex items-center gap-0.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={10} /> Enrolled
              </span>
            )}
          </div>

          {/* Title & description */}
          <h3 className="text-base font-bold text-foreground mb-2 leading-snug">{course.name}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4 line-clamp-3">{course.description}</p>

          {/* Progress bar (if enrolled) */}
          {enrolled && course.progress > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Progress</span>
                <span className="text-xs font-semibold text-foreground font-tabular">{course.progress}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full progress-bar-fill ${wingAccentMap[course.wing]}`}
                  style={{ width: `${course.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
            <span className="flex items-center gap-1"><Clock size={12} />{course.duration}</span>
            <span className="flex items-center gap-1"><BookOpen size={12} />{course.lessons} lessons</span>
            <span className="flex items-center gap-1"><Bot size={12} />{course.aiTutor}</span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {course.tags.map((tag) => (
              <span key={`tag-${course.id}-${tag}`} className="flex items-center gap-0.5 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                <Tag size={9} />{tag}
              </span>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={handleEnroll}
            disabled={enrolling}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold btn-scale transition-all ${
              course.locked
                ? 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100'
                : enrolled
                ? 'bg-primary/10 border border-primary/20 text-primary hover:bg-primary/15' :'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {course.locked ? (
              <><Lock size={14} /> Request Access</>
            ) : enrolled ? (
              <><PlayCircle size={14} /> Continue Learning</>
            ) : enrolling ? (
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <><PlayCircle size={14} /> Enroll Now</>
            )}
          </button>
        </div>
      </div>

      {/* Permission Request Modal */}
      <Modal
        open={permissionModalOpen}
        onClose={() => setPermissionModalOpen(false)}
        title="Request Access to Locked Course"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <Lock size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">{course.name}</p>
              <p className="text-xs text-amber-600 mt-0.5">This course requires parental or professional approval for ages {course.ageRange}.</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            A permission request will be sent to your parent or guardian. They will receive a notification and can approve or deny access from their dashboard.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setPermissionModalOpen(false)}
              className="flex-1 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // BACKEND INTEGRATION: POST /api/permissions/request with courseId, studentId, guardianId
                setPermissionModalOpen(false);
              }}
              className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold btn-scale hover:bg-amber-600 transition-colors"
            >
              Send Request
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}